import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Chip } from 'primereact/chip';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Toast } from 'primereact/toast';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';

import { InputText } from 'primereact/inputtext';
import EmployeeCreate from '../component/employee/create';
import EmployeeUpdate from '../component/employee/update';
import { useEmployee } from '../context/employee';

function EmployeesPage() {
    const { employee, employees, employeesLoaded } = useEmployee();
    const [adminNames, setAdminNames] = useState([]);

    const [globalFilterValue, setGlobalFilterValue] = useState('');

    const [rows, setRows] = useState([]);

    const [openCreate, setOpenCreate] = useState(false);
    const [openUpdate, setOpenUpdate] = useState(false);
    const [updateData, setUpdateData] = useState({});

    const toast = useRef(null);

    const onGlobalFilterChange = useCallback(
        (e) => {
            const needle = e.target.value.trim().toLowerCase();

            setGlobalFilterValue(needle);

            if (!needle) {
                setRows([...employees]);
                return;
            }

            setRows([
                ...employees.filter((haystack) =>
                    haystack.name.trim().toLowerCase().startsWith(needle)
                )
            ]);
        },
        [employees]
    );

    const renderHeader = () => {
        return (
            <div className="grid">
                <div className="col">
                    <Button
                        icon="pi pi-user-plus"
                        rounded
                        severity="success"
                        onClick={() => {
                            setOpenCreate(true);
                        }}
                    />
                </div>

                <div className="col">
                    Admins: <Chip label={adminNames.join(', ')} />
                </div>

                <div className="col">
                    <div className="flex justify-content-end">
                        <span className="p-input-icon-left">
                            <i className="pi pi-search" />
                            <InputText
                                value={globalFilterValue}
                                onChange={onGlobalFilterChange}
                                placeholder="Name Search"
                            />
                        </span>
                    </div>
                </div>
            </div>
        );
    };

    const ActionsDisplay = (employee) => {
        return (
            <>
                <Button
                    icon="pi pi-user-edit"
                    title={`Edit ${employee.username}`}
                    size="small"
                    rounded
                    severity="help"
                    onClick={() => {
                        setUpdateData(employee);
                        setOpenUpdate(true);
                    }}
                />

                <Link to={`/employee/${employee.username}`}>
                    <Button
                        icon="pi pi-user"
                        className="ml-1"
                        size="small"
                        severity="secondary"
                        rounded
                        title="Go to Profile"
                    />
                </Link>
            </>
        );
    };

    const header = renderHeader();

    const NotesDisplay = (employee) => {
        if (!employee.notes) {
            return '';
        }

        return <span dangerouslySetInnerHTML={{ __html: employee.notes }}></span>;
    };

    useEffect(() => {
        if (employeesLoaded) {
            setRows([...employees]);
            setAdminNames([...employees.filter((e) => e.admin).map((e) => e.name)]);
        }
    }, [employees, employeesLoaded]);

    if (!employee.admin) {
        return <Navigate to="/" />;
    }

    return (
        <Card title="Employees Management" className="pageCard">
            <DataTable
                value={rows}
                showGridlines
                header={header}
                removableSort
                paginator
                dataKey="_id"
                emptyMessage="No employees found"
                rows={5}
                rowsPerPageOptions={[5, 10, 25, 50]}
                loading={!employeesLoaded}
                rowClassName={(e) => {
                    return e.active ? '' : 'rowInactive';
                }}
            >
                <Column field="name" header="Name" sortable></Column>
                <Column field="jobTitle" header="Job Title" sortable></Column>
                <Column field="workExt" header="Work Ext" sortable></Column>
                <Column field="homePhone" header="Home Phone" sortable></Column>
                <Column header="Notes" body={NotesDisplay}></Column>
                <Column
                    header="Actions"
                    bodyStyle={{ width: '150px' }}
                    body={ActionsDisplay}
                ></Column>
            </DataTable>

            {!openCreate ? null : (
                <EmployeeCreate
                    open={openCreate}
                    onClose={(res) => {
                        if (res.status === 200) {
                            toast.current.show({
                                severity: 'success',
                                summary: 'Success',
                                detail: `Successfully Created ${res.data.employee.username}`,
                                life: 3000
                            });
                        }

                        setOpenCreate(false);
                    }}
                />
            )}

            {!openUpdate ? null : (
                <EmployeeUpdate
                    open={openUpdate}
                    data={updateData}
                    onClose={(res) => {
                        if (res.status === 200) {
                            toast.current.show({
                                severity: 'success',
                                summary: 'Success',
                                detail: `Successfully Updated ${res.data.employee.username}`,
                                life: 3000
                            });
                        }

                        setUpdateData({});
                        setOpenUpdate(false);
                    }}
                />
            )}

            <Toast ref={toast} />
        </Card>
    );
}

export default EmployeesPage;
